import { Language } from '@yleisradio/areena-types';
import {
  AddFavoriteError,
  AreenaMediaFetchError,
  AuthError,
  ConsentError,
  ErrorName,
  ExpiredSessionError,
  LoginApiError5xx,
  NetworkError,
  NotFoundError,
  RemoveFavoriteError,
  ServerError,
  SilentError,
  TimeoutError,
  UnknownError,
} from './errors';
import { errorMessages, getTranslation } from '../localizations/translations';
import { logger } from '../../common/logger';

export const shouldHandleWithErrorView = (e: unknown) => {
  if (e instanceof Error) {
    return (
      [
        ErrorName.SERVER_ERROR,
        ErrorName.TIMEOUT_ERROR,
        ErrorName.NETWORK_ERROR,
        ErrorName.EXPIRED_SESSION_ERROR,
      ] as string[]
    ).includes(e.name);
  }

  return false;
};

export const getErrorMessages = (
  errorName: string,
  language: Language,
  profileDisplayName: string | undefined
) => {
  switch (errorName) {
    case ErrorName.SERVER_ERROR:
      return {
        title: getTranslation('ErrorView_serverError_title', language),
        description: getTranslation(
          'ErrorView_serverError_description',
          language
        ),
      };

    case ErrorName.TIMEOUT_ERROR:
      return {
        title: getTranslation('ErrorView_timeoutError_title', language),
        description: getTranslation(
          'ErrorView_timeoutError_description',
          language
        ),
      };

    case ErrorName.NETWORK_ERROR:
      return {
        title: getTranslation('ErrorView_networkError_title', language),
        description: getTranslation(
          'ErrorView_networkError_description',
          language
        ),
      };

    case ErrorName.EXPIRED_SESSION_ERROR:
      return {
        title: `${getTranslation('ErrorView_sessionExpiredError_title', language)}${profileDisplayName ? ` ${profileDisplayName}` : ''}!`,
        description: getTranslation(
          'ErrorView_sessionExpiredError_description',
          language
        ),
      };

    case ErrorName.UNKNOWN_ERROR:
    default:
      return {
        title: getTranslation('ErrorView_unknownError_title', language),
        description: getTranslation(
          'ErrorView_unknownError_description',
          language
        ),
      };
  }
};

export function getErrorFromErrorName(
  errorName: string,
  message?: string,
  sessionCookie?: string,
  userId?: string
) {
  switch (errorName) {
    case ErrorName.AUTH_ERROR:
      return new AuthError(message);
    case ErrorName.AREENA_MEDIA_FETCH_ERROR:
      return new AreenaMediaFetchError(message);
    case ErrorName.NOT_FOUND_ERROR:
      return new NotFoundError(message);
    case ErrorName.CONSENT_ERROR:
      return new ConsentError(message);
    case ErrorName.SERVER_ERROR:
      return new ServerError(message);
    case ErrorName.TIMEOUT_ERROR:
      return new TimeoutError(message);
    case ErrorName.NETWORK_ERROR:
      return new NetworkError(message);
    case ErrorName.UNKNOWN_ERROR:
      return new UnknownError(message);
    case ErrorName.EXPIRED_SESSION_ERROR:
      return new ExpiredSessionError(message, sessionCookie, userId);
    case ErrorName.LOGIN_API_ERROR_5XX:
      return new LoginApiError5xx(message, sessionCookie);
    case ErrorName.ADD_FAVORITE_ERROR:
      return new AddFavoriteError(message);
    case ErrorName.REMOVE_FAVORITE_ERROR:
      return new RemoveFavoriteError(message);
    case ErrorName.SILENT_ERROR:
      return new SilentError(message);
    default:
      return new Error(message);
  }
}

export function logError(error: unknown, message?: string) {
  logger.error({ error }, message);
  if (error instanceof Error) {
    window.elasticApm?.captureError?.(error);
  }
}

export function logUnexpectedError(error: unknown) {
  logger.debug('Unexpected error');
  logger.error({ error });
  if (error instanceof Error) {
    window.elasticApm?.captureError?.(error);
  }
}

export function recoverErroneousSession({
  error,
  recoverLoginApiError,
  recoverAuthError,
  recoverExpiredSessionError,
}: {
  error: Error;
  recoverLoginApiError: (error: LoginApiError5xx) => void;
  recoverAuthError: () => void;
  recoverExpiredSessionError: (error: ExpiredSessionError) => void;
}) {
  if (error instanceof LoginApiError5xx) {
    recoverLoginApiError(error);
  } else if (error instanceof AuthError) {
    recoverAuthError();
  } else if (error instanceof ExpiredSessionError) {
    recoverExpiredSessionError(error);
  } else {
    logger.debug(`Nothing to recover from: ${error.name}`);
  }
}

export function logVisibleErrorToApm(error: Error) {
  const isErrorWithNotification = Object.keys(errorMessages).includes(
    error.name
  );
  const isUserRedirectedToErrorView = shouldHandleWithErrorView(error);

  if (!isErrorWithNotification && !isUserRedirectedToErrorView) return;

  window.elasticApm?.captureError?.(error);
}
